import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, ChevronDown, Loader2 } from 'lucide-react';
import { S } from '../constants';

const COUNTRY_CODES = {
  'Japan':'jp','Thailand':'th','South Korea':'kr','China':'cn','Taiwan':'tw',
  'Vietnam':'vn','Singapore':'sg','Indonesia':'id','Malaysia':'my','Philippines':'ph',
  'India':'in','Nepal':'np','Cambodia':'kh','Myanmar':'mm','Laos':'la','Sri Lanka':'lk',
  'France':'fr','Italy':'it','Spain':'es','United Kingdom':'gb','Germany':'de',
  'Netherlands':'nl','Switzerland':'ch','Austria':'at','Greece':'gr','Portugal':'pt',
  'Czech Republic':'cz','Hungary':'hu','Poland':'pl','Belgium':'be','Sweden':'se',
  'Norway':'no','Denmark':'dk','Finland':'fi','Ireland':'ie','Croatia':'hr',
  'Romania':'ro','Bulgaria':'bg','Slovakia':'sk','Slovenia':'si','Serbia':'rs',
  'United States':'us','Canada':'ca','Mexico':'mx','Cuba':'cu','Costa Rica':'cr',
  'Panama':'pa','Jamaica':'jm','Dominican Republic':'do',
  'Brazil':'br','Argentina':'ar','Peru':'pe','Colombia':'co','Chile':'cl',
  'Bolivia':'bo','Ecuador':'ec','Uruguay':'uy','Paraguay':'py','Venezuela':'ve',
  'South Africa':'za','Morocco':'ma','Egypt':'eg','Kenya':'ke','Tanzania':'tz',
  'Ethiopia':'et','Ghana':'gh','Nigeria':'ng','Tunisia':'tn','Uganda':'ug',
  'United Arab Emirates':'ae','Turkey':'tr','Jordan':'jo','Israel':'il',
  'Qatar':'qa','Saudi Arabia':'sa','Oman':'om','Kuwait':'kw','Bahrain':'bh',
  'Australia':'au','New Zealand':'nz','Fiji':'fj','Maldives':'mv','Samoa':'ws',
  'Papua New Guinea':'pg','Vanuatu':'vu',
};

const searchNominatim = async (q, countryCode = '') => {
  try {
    const cc = countryCode ? `&countrycodes=${countryCode}` : '';
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=6&addressdetails=1&accept-language=en${cc}`,
      { headers: { 'User-Agent': 'EC-Travel/1.0' } }
    );
    const data = await res.json();
    return data
      .filter(r => r.class === 'place' || ['city','town','village','municipality','suburb','island','district'].includes(r.type))
      .slice(0, 5)
      .map(r => ({
        name: r.name || r.display_name.split(',')[0].trim(),
        display: [r.name, r.address?.state, r.address?.country].filter(Boolean).join(', '),
      }));
  } catch { return []; }
};

export const DD = ({ label, options=[], value, onChange, icon:Ic, disabled=false, placeholder='—', allowCustom=false, country='', onClear }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const [query, setQuery] = useState('');
  const [nomResults, setNomResults] = useState([]);
  const [nomLoading, setNomLoading] = useState(false);
  const nomTimer = useRef(null);
  const ref = useRef(null), btn = useRef(null), inputRef = useRef(null);

  useEffect(() => {
    const h = e => { if(ref.current&&!ref.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = () => {
    if(disabled) return;
    if(!open && btn.current) {
      const r = btn.current.getBoundingClientRect();
      setPos({ position:'fixed', top:r.bottom+4, left:r.left, width:Math.max(r.width, 200), zIndex:9999 });
    }
    setOpen(v => { if(!v) { setQuery(''); setTimeout(()=>inputRef.current?.focus(), 50); } return !v; });
  };

  useEffect(() => {
    if (!allowCustom || !open) return;
    const q = query.trim();
    if (q.length < 2) { setNomResults([]); return; }
    const localMatches = options.filter(o => (o.label||o).toLowerCase().includes(q.toLowerCase()));
    if (localMatches.length >= 3) { setNomResults([]); return; }
    if (nomTimer.current) clearTimeout(nomTimer.current);
    setNomLoading(true);
    nomTimer.current = setTimeout(async () => {
      const cc = COUNTRY_CODES[country] || '';
      const results = await searchNominatim(q, cc);
      const optionNames = options.map(o => (o.label||o).toLowerCase());
      setNomResults(results.filter(r => !optionNames.includes(r.name.toLowerCase())));
      setNomLoading(false);
    }, 500);
    return () => { if (nomTimer.current) clearTimeout(nomTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, allowCustom]);

  const sel = options.find(o=>o.key===value||o===value);
  const disp = sel ? (sel.label||sel) : (value || placeholder);
  const filtered = query.trim()
    ? options.filter(o => (o.label||o).toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div ref={ref} style={{ position:'relative', opacity:disabled?0.45:1, pointerEvents:disabled?'none':'auto' }}>
      {label && <p style={S.label}>{label}</p>}
      <button ref={btn} onClick={toggle} style={{ ...S.ddBtn, borderColor:open?'#f59e0b':value?'#f59e0b':'#e2e8f0', background:value?'#fffbeb':'white', borderWidth: value?2:1.5 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
          {Ic && <Ic size={15} color="#f59e0b"/>}
          <span style={{ color:value?'#92400e':'#94a3b8', fontSize:13, fontWeight:value?800:400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{disp}</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
          {value && <CheckCircle2 size={14} color="#f59e0b"/>}
          <ChevronDown size={14} color="#94a3b8" style={{ transform:open?'rotate(180deg)':'none', transition:'0.2s' }}/>
        </div>
      </button>
      {value && (onClear || onChange) && (
        <button onClick={e=>{ e.stopPropagation(); (onClear||onChange)(''); setOpen(false); }}
          style={{ position:'absolute', right:32, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', padding:'2px 4px', lineHeight:1, color:'#94a3b8', fontSize:14, fontWeight:700 }}
          title="清除">×</button>
      )}
      {open && (
        <div style={{ ...pos, background:'white', border:'1.5px solid #e2e8f0', borderRadius:14, boxShadow:'0 20px 50px rgba(0,0,0,0.13)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'8px 10px', borderBottom:'1px solid #f1f5f9', flexShrink:0 }}>
            <div style={{ position:'relative' }}>
              <span style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', fontSize:12 }}>🔍</span>
              <input ref={inputRef} value={query} onChange={e=>setQuery(e.target.value)}
                placeholder={label||'Search...'}
                style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:9, padding:'7px 10px 7px 28px', fontSize:12, fontWeight:600, outline:'none', color:'#1e293b', boxSizing:'border-box' }}
                onFocus={e=>e.target.style.borderColor='#f59e0b'}
                onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
            </div>
          </div>
          <div style={{ maxHeight:240, overflowY:'auto' }}>
            {filtered.length === 0 && !allowCustom && !nomLoading && nomResults.length === 0 && (
              <p style={{ textAlign:'center', fontSize:12, color:'#94a3b8', padding:'16px 0', margin:0 }}>No results</p>
            )}
            {filtered.map((o,i) => {
              const k=o.key??o, l=o.label??o;
              return (
                <button key={i} onClick={()=>{onChange(k);setOpen(false);setQuery('');setNomResults([]);}}
                  style={{ display:'flex', width:'100%', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'none', border:'none', color:k===value?'#f59e0b':'#475569', fontWeight:k===value?900:600, fontSize:13, cursor:'pointer' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#fffbeb'}
                  onMouseLeave={e=>e.currentTarget.style.background='none'}>
                  {l} {k===value&&<CheckCircle2 size={13} color="#f59e0b"/>}
                </button>
              );
            })}
            {allowCustom && query.trim().length >= 2 && (nomLoading || nomResults.length > 0) && (
              <div style={{ borderTop: filtered.length ? '1px solid #f1f5f9' : 'none' }}>
                {nomLoading ? (
                  <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px' }}>
                    <Loader2 size={12} color="#94a3b8" style={{ animation:'spin 1s linear infinite' }}/>
                    <span style={{ fontSize:11, color:'#94a3b8' }}>搜尋城市…</span>
                  </div>
                ) : nomResults.map((r,i) => (
                  <button key={`nom-${i}`} onClick={()=>{onChange(r.name);setOpen(false);setQuery('');setNomResults([]);}}
                    style={{ display:'flex', width:'100%', alignItems:'center', gap:8, padding:'9px 14px', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}
                    onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
                    onMouseLeave={e=>e.currentTarget.style.background='none'}>
                    <span style={{ fontSize:12 }}>🌐</span>
                    <div>
                      <p style={{ fontSize:12, fontWeight:700, color:'#0f172a', margin:0 }}>{r.name}</p>
                      <p style={{ fontSize:10, color:'#94a3b8', margin:0 }}>{r.display}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {allowCustom && query.trim().length >= 2 && !nomLoading &&
              !filtered.some(o=>(o.label||o).toLowerCase()===query.trim().toLowerCase()) &&
              !nomResults.some(r=>r.name.toLowerCase()===query.trim().toLowerCase()) && (
              <button onClick={()=>{onChange(query.trim());setOpen(false);setQuery('');setNomResults([]);}}
                style={{ display:'flex', width:'100%', alignItems:'center', gap:8, padding:'10px 14px', background:'none', border:'none', borderTop:'1px dashed #e2e8f0', color:'#475569', fontWeight:700, fontSize:12, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e=>e.currentTarget.style.background='none'}>
                <span style={{ fontSize:13 }}>✏️</span>
                <span>直接使用「<strong style={{ color:'#0f172a' }}>{query.trim()}</strong>」</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const CompactDD = ({ options, value, onChange, icon:Ic }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const ref = useRef(null), btn = useRef(null);
  useEffect(() => {
    const h = e => { if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = () => {
    if(!open&&btn.current){const r=btn.current.getBoundingClientRect();setPos({position:'fixed',top:r.bottom+4,right:window.innerWidth-r.right,zIndex:9999,minWidth:170});}
    setOpen(v=>!v);
  };
  const sel = options.find(o=>o.key===value);
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button ref={btn} onClick={toggle} style={{ display:'flex',alignItems:'center',gap:5,padding:'7px 11px',borderRadius:10,background:'none',border:'none',color:'rgba(15,23,42,0.7)',cursor:'pointer',fontSize:12,fontWeight:700 }}>
        {Ic&&<Ic size={13}/>} {sel?.label||value} <ChevronDown size={11} style={{ transform:open?'rotate(180deg)':'none',transition:'0.2s' }}/>
      </button>
      {open&&(
        <div style={{ ...pos, background:'#1e293b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:12,padding:'4px 0',boxShadow:'0 20px 50px rgba(0,0,0,0.4)', maxHeight:280, overflowY:'auto' }}>
          {options.map((o,i)=>(
            <button key={i} onClick={()=>{onChange(o.key);setOpen(false);}}
              style={{ display:'block',width:'100%',textAlign:'left',padding:'8px 15px',background:'none',border:'none',cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.08)'}
              onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <span style={{ color:o.key===value?'#fbbf24':'rgba(255,255,255,0.85)',fontSize:12,fontWeight:700,display:'block' }}>{o.label}</span>
              {o.sublabel && <span style={{ color:'rgba(255,255,255,0.4)',fontSize:10,fontWeight:500,display:'block',marginTop:1 }}>{o.sublabel}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
